import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Upload, Download, Trash2, FileText } from 'lucide-react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nProvider';
import { useSession } from '../../../session/SessionProvider';
import { errorText } from '../../../lib/errors';
import { Button, DataTable, Badge, Modal, Field, Input, Select, ConfirmDialog, EmptyState } from '../../../ui';

const DOC_TYPES = ['contract', 'act', 'invoice', 'other'];

const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/** Документы компании (договоры, акты, счета). Файлы скачиваются только через авторизованный запрос. */
export default function DocumentsTab({ tenant }) {
  const { t, fmt } = useI18n();
  const { isOwner, canEdit } = useSession();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const base = `/admin/tenants/${tenant.id}/documents`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(base);
      setDocs(res.data.data || []);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [base, t]);

  useEffect(() => { load(); }, [load]);

  const download = async (doc) => {
    try {
      const res = await api.get(`${base}/${doc.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.title;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(t('common.error'));
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`${base}/${deleting.id}`);
      toast.success(t('documents.deleted'));
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'title',
      header: t('documents.docTitle'),
      sortable: true,
      render: (d) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
            <FileText className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">{d.title}</p>
            <p className="truncate text-xs text-zinc-500">{d.file_name}</p>
          </div>
        </div>
      ),
    },
    { key: 'doc_type', header: t('documents.type'), sortable: true, render: (d) => <Badge>{t(`documents.types.${d.doc_type}`)}</Badge> },
    { key: 'file_size', header: t('documents.size'), align: 'right', hideOnMobile: true, render: (d) => <span className="tabular text-zinc-500">{formatSize(d.file_size)}</span> },
    { key: 'created_at', header: t('common.created'), sortable: true, hideOnMobile: true, render: (d) => <span className="tabular text-zinc-500">{fmt.date(d.created_at)}</span> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'right',
      render: (d) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon-sm" onClick={() => download(d)} aria-label={`${t('documents.download')} ${d.title}`}>
            <Download className="h-4 w-4" />
          </Button>
          {isOwner && (
            <Button variant="danger-ghost" size="icon-sm" onClick={() => setDeleting(d)} aria-label={`${t('common.delete')} ${d.title}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {canEdit && (
        <div className="mb-4 flex justify-end">
          <Button icon={Upload} onClick={() => setUploadOpen(true)}>{t('documents.upload')}</Button>
        </div>
      )}
      <DataTable
        columns={columns}
        rows={docs}
        loading={loading}
        empty={<EmptyState icon={FileText} title={t('documents.empty')} text={t('documents.emptyHint')} />}
      />
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} base={base} onDone={load} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        loading={busy}
        title={t('documents.deleteTitle')}
        text={t('documents.deleteText', { name: deleting?.title })}
        confirmLabel={t('common.delete')}
      />
    </>
  );
}

function UploadModal({ open, onClose, base, onDone }) {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [type, setType] = useState('contract');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setTitle(''); setType('contract'); setFile(null); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSaving(true);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('title', title || file.name);
      body.append('doc_type', type);
      await api.post(base, body);
      toast.success(t('documents.uploaded'));
      onClose();
      onDone();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('documents.uploadTitle')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="upload-doc-form" loading={saving} disabled={!file}>{t('documents.upload')}</Button>
        </>
      }
    >
      <form id="upload-doc-form" onSubmit={submit} className="space-y-4">
        <Field label={t('documents.file')} required>
          <input
            type="file"
            required
            onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, '')); }}
            className="block w-full text-sm text-zinc-600 file:mr-3 file:rounded-lg file:border-0 file:bg-zinc-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-zinc-800 hover:file:bg-zinc-200 dark:text-zinc-400 dark:file:bg-zinc-800 dark:file:text-zinc-100"
          />
        </Field>
        <Field label={t('documents.docTitle')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </Field>
        <Field label={t('documents.type')}>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            {DOC_TYPES.map((d) => <option key={d} value={d}>{t(`documents.types.${d}`)}</option>)}
          </Select>
        </Field>
      </form>
    </Modal>
  );
}
